import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import * as Forge from 'node-forge';
import { Subscription } from 'rxjs';
import { AuthorizationService } from '../../../../access/shared/authorization/authorization.service';
import { Permissions } from '../../../../access/shared/authorization/permissions';
import { NotificationService } from '../../../../notification/shared/notification.service';
import { Dav } from '../../../dav/shared/dav';
import { DavService } from '../../../dav/shared/dav.service';
import { Site } from '../../../dav/shared/site';
import { Client } from '../../../shared/client/client';
import { ClientService } from '../../../shared/client/client.service';
import { DavForm } from '../shared/dav-form';

@Component({
  selector: 'app-dav-param-edit',
  templateUrl: './dav-param-edit.component.html',
  styleUrls: ['./dav-param-edit.component.scss']
})
export class DavParamEditComponent implements OnInit, OnDestroy {
  displayedColumns = ['site', 'client', 'action'];

  private siteSub: Subscription = new Subscription();
  private clientSub: Subscription = new Subscription();

  sites: Array<Site> = new Array<Site>();

  clientFilter: string = "";
  clients: Array<Client> = new Array<Client>();

  //interface with matTable
  matInterfaceSelected: MatTableDataSource<Client> = new MatTableDataSource<Client>();
  matInterface: MatTableDataSource<Client> = new MatTableDataSource<Client>();

  //current list selection
  selectedFilteredClient: Array<Client> = new Array<Client>();

  formsControl: DavForm
  dav: Dav
  rsa
  passwordDefault = "__________"
  passwordEncrypted;
  readonly: boolean = true
  showPassword = true
  currentSite = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private notification: NotificationService,
    private davService: DavService,
    private authService: AuthorizationService
  ) { }

  ngOnInit() {
    this.readonly = !this.authService.hasPermission(Permissions.ROLE_ADMIN_GLOBAL);

    this.formsControl = new DavForm();
    this.clients = new Array<Client>();

    this.formsControl.get('clients').valueChanges.subscribe((clients: Array<Client>) => {
      if (clients != null) {
        this.matInterfaceSelected.data = this.sortClients(clients);
        this.updateFilter();
      }
    });

    this.route.data.subscribe(data => {
      this.dav = data["dav"];
      if (this.dav) {
        this.passwordEncrypted = this.dav.password;
        this.dav.password = this.passwordDefault;
        this.formsControl.patchValue(this.dav);
        this.clientSub = this.davService.getClients(this.dav.site, this.clientFilter).subscribe(clients => {
          this.clients = this.sortClients(clients);
          this.updateFilter();
        });
      }
    });

    this.getSites();

    this.formsControl.get('site').valueChanges.subscribe(site => {
      if (site !== undefined) {
        if (site.numero != "0") {
          this.clientSub = this.davService.getClients(site, this.clientFilter).subscribe(clients => {
            this.clients = this.sortClients(clients);
            this.updateFilter();
            this.formsControl.removeClients();
          });
        }
      }
    });
  }

  getSites() {
    this.siteSub = this.davService.getSites().subscribe(sites => {
      // Suppression du site 0 (tous les sites)
      this.sites = sites.filter(site => site.numero != '0');
      this.sites = this.sites.sort((a: Site, b: Site) => {
        if (a.numero > b.numero) return 1;
        if (a.numero < b.numero) return -1;
        else return 0;
      });
    });
  }

  compareSite(a: Site, b: Site): boolean {
    return a != undefined && b != undefined && a.id === b.id;
  }

  filterPredicate(client: Client, filter: string, data: Array<Client>, davId: string): boolean {
    return this.formsControl.filterPredicate(client, filter, data, davId);
  }

  unselectClient(client: Client) {
    this.formsControl.unselectClient(client);
  }

  private compareClient(c: Client, c2: Client): number {
    if (c.site.numero != c2.site.numero) {
      if (c.site.numero < c2.site.numero) return -1;
      else return 1;
    } else {
      if (c.code_client == c2.code_client) {
        if (c.code_service > c2.code_service) return 1;
        else return -1;
      }
      if (c.code_client > c2.code_client) return 1;
      if (c.code_client < c2.code_client) return -1;
    }
    return 0;
  }

  selectClient(client: Client) {
    this.formsControl.selectClient(client);
  }

  updateFilter() {
    let davId = this.dav && "id" in this.dav ? this.dav.id : null
    this.matInterface.data = this.clients.filter(client => this.filterPredicate(client, this.clientFilter, this.matInterfaceSelected.data, davId)).slice(0, 100);
  }

  submitForm() {
    if (this.formsControl.valid) {
      let dav = this.formsControl.value;
      let password = dav.password;
      let isModif = false;
      if (this.dav != undefined) {
        dav.id = this.dav.id
        isModif = true
      }
      if (this.passwordEncrypted && dav.password == this.passwordDefault) {
        dav.password = this.passwordEncrypted;
        this.saveDav(dav, isModif, password);
      } else {
        // recup publicKey
        this.davService.getKey().subscribe(key => {
          this.rsa = Forge.pki.publicKeyFromPem(key)
          // encrypt password
          dav.password = window.btoa(this.rsa.encrypt(dav.password))
          this.saveDav(dav, isModif, password);
        });
      }
    } else {
      if (this.formsControl.hasError('minElement', ['clients'])) {
        this.notification.notif("Vous devez choisir au moins un client", 'error');
      } else {
        this.notification.notif("Le formulaire n'est pas valide", 'error');
      }
    }
  }

  getErrorMessage(formControlName) {
    if (this.formsControl.get(formControlName).hasError('required')) {
      return "Cette valeur est requise";
    } else if (this.formsControl.get(formControlName).hasError('pattern')) {
      return "Le format n'est pas correct"
    }
  }

  get adresse_serveur(): AbstractControl {
    return this.formsControl.get("adresse_serveur");
  }

  get login(): AbstractControl {
    return this.formsControl.get("login");
  }

  get password(): AbstractControl {
    return this.formsControl.get("password");
  }

  get nom(): AbstractControl {
    return this.formsControl.get("nom");
  }

  get partenaire(): AbstractControl {
    return this.formsControl.get("partenaire");
  }

  get site(): AbstractControl {
    return this.formsControl.get("site");
  }

  ngOnDestroy() {
    this.siteSub.unsubscribe();
    this.clientSub.unsubscribe();
  }

  saveDav(dav, isModif, password) {
    this.davService.saveDav(dav).subscribe(res => {
      if (isModif) {
        this.notification.notif("Les modifications sont enregistrées", 'notif');
      } else {
        this.notification.notif("Le nouveau DAV a été enregistré", 'notif');
      }
      this.router.navigate(['..'], { relativeTo: this.route });
    }, error => {
      dav.password = password;
      console.log(error)
    });
  }

  sortClients(clients) {
    return clients.sort((a: Client, b: Client) => {
      if (a != null && b != null) {
        return this.compareClient(a, b);
      }
      return 0;
    });
  }

  getIsDisabled() {
    return this.password.value == this.passwordDefault;
  }

  toggleVisibility() {
    if (!this.getIsDisabled()) {
      this.showPassword = !this.showPassword
    }
  }
}
